import { z } from 'zod';
import { agentProtocolSchema, agentTypeSchema } from '../enums/agent.js';
import { StatusTypeSchema } from '../enums/status.js';
import { AccountAuditSchema } from '../types/account.js';
import { agentProtocolDataSchema } from '../types/agent.protocol.js';
import {
  agentDetailsSchema,
  customAgentSchema,
  csvAgentSchema,
  orchestrationAgentSchema,
  subAgentTableItemSchema,
} from '../types/agents.js';
import { ComplianceConfigSchema } from '../types/compliance.js';
import { UserAuditSchema } from '../types/user.js';
import { FilterSchema } from './filters.js';

export const agentsRecordSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  description: z.string(),
  status: StatusTypeSchema,
  protocol: agentProtocolSchema,
  typeId: agentTypeSchema,
  createdOn: z.coerce.date(),
  tags: z.array(z.string()),
});

export const getAgentsResponseSchema = z.object({
  items: z.array(agentsRecordSchema),
});

export const createAgentResponseSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  description: z.string(),
  protocolData: agentProtocolDataSchema,
  typeId: agentTypeSchema,
  createdOn: z.coerce.date(),
});

export const getAgentRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});

export const getAgentResponseSchema = agentDetailsSchema;

export const getAgentUsersResponseSchema = z.object({
  users: z.array(UserAuditSchema),
});

export const getAgentComplianceResponseSchema = z.object({
  complianceConfig: ComplianceConfigSchema.optional().nullable(),
});

export const getAgentAccountsResponseSchema = z.object({
  accounts: z.array(AccountAuditSchema),
});

export const updateAgentRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});
export const updateAgentRequestBodySchema = z.union([
  customAgentSchema.omit({
    identifier: true,
    id: true,
    status: true,
    createdOn: true,
    files: true,
    complianceConfig: true,
  }),
  csvAgentSchema.omit({
    identifier: true,
    id: true,
    status: true,
    createdOn: true,
    files: true,
    complianceConfig: true,
  }),
  orchestrationAgentSchema.omit({
    identifier: true,
    id: true,
    status: true,
    createdOn: true,
    files: true,
    complianceConfig: true,
  }),
]);
export const createAgentRequestBodySchema = updateAgentRequestBodySchema;

export const updateAgentStatusRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});
export const updateAgentStatusRequestBodySchema = z.object({
  status: StatusTypeSchema,
});

export const deleteAgentRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});

export const addAgentSubAgentsRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});
export const addAgentSubAgentsRequestBodySchema = z.object({
  subAgentIds: z.array(z.coerce.number()),
});

export const removeAgentSubAgentsRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});
export const removeAgentSubAgentsRequestBodySchema = z.object({
  subAgentIds: z.array(z.coerce.number()),
});

export const getAgentSubAgentsRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});
export const getAgentSubAgentsResponseBodySchema = z.object({
  items: z.array(subAgentTableItemSchema),
});

export const uploadAgentFileRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});
export const uploadAgentFileResponseSchema = z.object({
  file: z.object({
    id: z.number(),
    bucket: z.string(),
    key: z.string(),
    name: z.string(),
  }),
});

export namespace GetAgentDataSources {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
  });

  export type Params = z.infer<typeof paramsSchema>;

  export const responseSchema = z.object({
    dataSources: z.array(
      z.object({
        id: z.coerce.number(),
        name: z.string(),
      })
    ),
  });

  export type Response = z.infer<typeof responseSchema>;
}

export namespace AddAgentDataSources {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
    dataSourceId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({});

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.void();

  export type Response = z.infer<typeof responseSchema>;
}

export namespace DeleteAgentDataSources {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
    dataSourceId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({
    dataSources: z.array(z.coerce.number()),
  });

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.void();

  export type Response = z.infer<typeof responseSchema>;
}

export namespace GetAgentFilters {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
  });

  export type Params = z.infer<typeof paramsSchema>;

  export const responseSchema = z.object({
    filters: z.array(FilterSchema),
  });

  export type Response = z.infer<typeof responseSchema>;
}

export namespace AddAgentFilters {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
    filterId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({});

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.void();

  export type Response = z.infer<typeof responseSchema>;
}

export namespace DeleteAgentFilters {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
    filterId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({});

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.void();

  export type Response = z.infer<typeof responseSchema>;
}

export namespace UpdateAgentCompliance {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({
    complianceConfig: ComplianceConfigSchema.optional().nullable(),
  });

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.void();

  export type Response = z.infer<typeof responseSchema>;
}

export namespace MassUpdateAgentTags {
  export const requestBodySchema = z.object({
    agentIds: z.array(z.coerce.number()),
    tags: z.array(z.string()),
  });

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.void();

  export type Response = z.infer<typeof responseSchema>;
}

export namespace GetAgentCompiledPrompt {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const querySchema = z.object({
    brandId: z.coerce.number(),
  });
  export type Query = z.infer<typeof querySchema>;

  const baseSectionSchema = z.object({
    label: z.string(),
    content: z.string(),
  });

  const sectionSchema = baseSectionSchema.extend({
    subSections: z.array(baseSectionSchema).optional(),
  });

  export const responseSchema = z.object({
    compiledPrompt: z.string(),
    sections: z.array(sectionSchema),
  });
  export type Response = z.infer<typeof responseSchema>;
}

export namespace GetAgentConfigSummary {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const querySchema = z.object({
    brandId: z.coerce.number().optional(),
  });
  export type Query = z.infer<typeof querySchema>;

  export const responseSchema = z.object({
    systemPrompt: z.string().nullable(),
    model: z.string().nullable(),
    streamResponse: z.boolean().nullable(),
    skillCount: z.number(),
    toolCount: z.number(),
    filterCount: z.number(),
    dataSourceCount: z.number(),
    queryCount: z.number(),
  });
  export type Response = z.infer<typeof responseSchema>;
}

export namespace GetAgentsByContext {
  export const querySchema = z
    .object({
      applicationId: z.coerce.number().optional(),
      brandId: z.coerce.number().optional(),
    })
    .refine((d) => (d.applicationId === undefined) !== (d.brandId === undefined), {
      message: 'Exactly one of applicationId or brandId is required',
    });
  export type Query = z.infer<typeof querySchema>;

  const chatContextSchema = z.object({
    id: z.number(),
    name: z.string(),
  });

  const applicationSchema = z.object({
    id: z.number(),
    name: z.string(),
  });

  export const agentRowSchema = z.object({
    id: z.number(),
    name: z.string(),
    description: z.string(),
    chatContexts: z.array(chatContextSchema),
    applications: z.array(applicationSchema),
  });

  export const responseSchema = z.object({
    agents: z.array(agentRowSchema),
  });
  export type Response = z.infer<typeof responseSchema>;
}

const agentEnvironmentSyncPromptVersionSchema = z.object({
  identifier: z.uuid(),
  number: z.number().int().positive(),
  status: z.enum(['published', 'draft', 'archived']),
  inputSchema: z.record(z.string(), z.unknown()),
  messages: z.array(z.unknown()),
  llmModel: z.string(),
  structuredOutputSchema: z.record(z.string(), z.unknown()).nullable(),
  lastNMessagePairs: z.number().int().nullable(),
});

const agentEnvironmentSyncPromptSchema = z.object({
  id: z.number().int(),
  identifier: z.uuid(),
  name: z.string(),
  description: z.string(),
  kind: z.enum([
    'Default',
    'AgentSystemPrompt',
    'ComplianceCheck',
    'ChatNameGeneration',
    'SuggestedFollowupsGeneration',
    'QueryEntityDetection',
  ]),
  version: agentEnvironmentSyncPromptVersionSchema.nullable(),
});

const agentEnvironmentSyncAgentResourceSchema = z.object({
  id: z.number().int(),
  identifier: z.string(),
  name: z.string(),
  description: z.string(),
  data: z.record(z.string(), z.unknown()),
  inputSchema: z.record(z.string(), z.unknown()),
  complianceConfig: z.record(z.string(), z.unknown()).nullable(),
  systemPrompt: z.string().nullable(),
  systemPromptId: z.uuid().nullable(),
  prompt: agentEnvironmentSyncPromptSchema.nullable(),
});

const agentEnvironmentSyncSkillResourceSchema = z.object({
  sourceAttachmentId: z.uuid(),
  skill: z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
  }),
  version: z.object({
    id: z.uuid(),
    skillId: z.uuid(),
    number: z.number().int().positive(),
    contentMd: z.string(),
    description: z.string().nullable(),
  }),
  attachment: z.object({
    brandId: z.number().int().nullable(),
    isEnabled: z.boolean(),
  }),
});

const agentEnvironmentSyncFeatureSettingSchema = z.object({
  propName: z.string(),
  value: z.string(),
  brandId: z.number().int().nullable(),
});

const agentEnvironmentSyncSettingsResourceSchema = z.object({
  features: z.array(agentEnvironmentSyncFeatureSettingSchema),
  complianceConfig: z.record(z.string(), z.unknown()).nullable(),
});

const agentEnvironmentSyncDataSourceResourceSchema = z.object({
  sourceAttachmentId: z.uuid(),
  dataSourceId: z.uuid(),
  sourceIdentifier: z.string(),
  name: z.string(),
  dataConnection: z.object({
    identifier: z.uuid(),
    name: z.string(),
    connectionProvider: z.string(),
  }),
  source: z.object({
    description: z.string().nullable(),
    sourceQueryGuidance: z.string().nullable(),
  }),
  attachment: z.object({
    brandId: z.number().int().nullable(),
    isEnabled: z.boolean(),
  }),
});

const agentEnvironmentSyncDataQueryResourceSchema = z.object({
  queryAttachmentId: z.uuid(),
  queryId: z.uuid(),
  name: z.string(),
  description: z.string(),
  queryKind: z.enum(['template', 'guided']),
  templateDialect: z.string().nullable(),
  dataConnection: z.object({
    identifier: z.uuid(),
    name: z.string(),
    connectionProvider: z.string(),
  }),
  query: z.object({
    queryJson: z.record(z.string(), z.unknown()).nullable(),
    associatedStoreSourceIdentifiers: z.array(z.string()),
  }),
  attachment: z.object({
    brandId: z.number().int().nullable(),
    isEnabled: z.boolean(),
  }),
});

const agentEnvironmentSyncPersistedToolResourceSchema = z.object({
  toolId: z.number().int(),
  toolIdentifier: z.string(),
  name: z.string(),
  attachment: z.object({
    brandId: z.number().int().nullable(),
    isEnabled: z.boolean(),
    toolGuidance: z.string().nullable(),
  }),
});

const agentEnvironmentSyncRuntimeToolResourceSchema = z.object({
  toolId: z.number().int(),
  toolIdentifier: z.string(),
  name: z.string(),
  derivedFromFeature: z.string().nullable(),
  isEnabled: z.boolean(),
});

export namespace ExportAgentConfig {
  export const paramsSchema = z.object({
    agentId: z.coerce.number().int().positive(),
  });

  export type Params = z.infer<typeof paramsSchema>;

  export const responseSchema = z.object({
    schemaVersion: z.literal('0.2.0'),
    exportedAt: z.string(),
    agent: agentEnvironmentSyncAgentResourceSchema,
    skills: z.array(agentEnvironmentSyncSkillResourceSchema),
    settings: agentEnvironmentSyncSettingsResourceSchema.optional(),
    dataSources: z.array(agentEnvironmentSyncDataSourceResourceSchema).optional(),
    dataQueries: z.array(agentEnvironmentSyncDataQueryResourceSchema).optional(),
    persistedTools: z.array(agentEnvironmentSyncPersistedToolResourceSchema).optional(),
    runtimeTools: z.array(agentEnvironmentSyncRuntimeToolResourceSchema).optional(),
  });

  export type Response = z.infer<typeof responseSchema>;
}

export namespace ImportAgentConfig {
  export const paramsSchema = ExportAgentConfig.paramsSchema;

  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = ExportAgentConfig.responseSchema;

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.object({
    agentImportSuccess: z.boolean(),
    noOp: z.boolean(),
    skillsCreated: z.number().int(),
    skillsUpdated: z.number().int(),
    attachmentsCreated: z.number().int(),
    attachmentsUpdated: z.number().int(),
    attachmentsSkipped: z.number().int(),
    attachmentsSkippedBrandUnavailable: z.number().int(),
  });

  export type Response = z.infer<typeof responseSchema>;
}

const agentConfigDiffFieldSchema = z.object({
  path: z.string(),
  label: z.string(),
  current: z.unknown(),
  incoming: z.unknown(),
});

const agentConfigDiffStatusSchema = z.enum(['unchanged', 'created', 'updated', 'removed']);
const agentConfigDiffSkipReasonSchema = z.enum(['brand_unavailable']);

export namespace DiffAgentConfig {
  export const paramsSchema = ExportAgentConfig.paramsSchema;

  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = ImportAgentConfig.requestBodySchema;

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.object({
    agent: z.object({
      changed: z.boolean(),
      fields: z.array(agentConfigDiffFieldSchema),
    }),
    prompt: z.object({
      status: agentConfigDiffStatusSchema,
      fields: z.array(agentConfigDiffFieldSchema),
    }),
    skills: z.array(
      z.object({
        key: z.string(),
        skillId: z.uuid(),
        brandId: z.number().int().nullable(),
        name: z.string(),
        status: agentConfigDiffStatusSchema,
        brandAvailable: z.boolean(),
        skipped: z.boolean(),
        skipReason: agentConfigDiffSkipReasonSchema.nullable(),
        fields: z.array(agentConfigDiffFieldSchema),
      })
    ),
    settings: z
      .object({
        status: agentConfigDiffStatusSchema,
        fields: z.array(agentConfigDiffFieldSchema),
      })
      .optional(),
    dataSources: z
      .array(
        z.object({
          key: z.string(),
          dataSourceId: z.uuid(),
          brandId: z.number().int().nullable(),
          name: z.string(),
          status: agentConfigDiffStatusSchema,
          brandAvailable: z.boolean(),
          skipped: z.boolean(),
          skipReason: agentConfigDiffSkipReasonSchema.nullable(),
          fields: z.array(agentConfigDiffFieldSchema),
        })
      )
      .optional(),
    dataQueries: z
      .array(
        z.object({
          key: z.string(),
          dataConnectionQueryId: z.uuid(),
          brandId: z.number().int().nullable(),
          name: z.string(),
          status: agentConfigDiffStatusSchema,
          brandAvailable: z.boolean(),
          skipped: z.boolean(),
          skipReason: agentConfigDiffSkipReasonSchema.nullable(),
          fields: z.array(agentConfigDiffFieldSchema),
        })
      )
      .optional(),
    persistedTools: z
      .array(
        z.object({
          key: z.string(),
          toolId: z.number().int(),
          brandId: z.number().int().nullable(),
          name: z.string(),
          status: agentConfigDiffStatusSchema,
          fields: z.array(agentConfigDiffFieldSchema),
        })
      )
      .optional(),
    runtimeTools: z
      .object({
        currentOnly: z.array(agentEnvironmentSyncRuntimeToolResourceSchema),
        incomingOnly: z.array(agentEnvironmentSyncRuntimeToolResourceSchema),
      })
      .optional(),
    missingFromIncoming: z.array(
      z.object({
        key: z.string(),
        skillId: z.uuid(),
        brandId: z.number().int().nullable(),
        name: z.string(),
        status: z.literal('removed'),
        fields: z.array(agentConfigDiffFieldSchema),
      })
    ),
    summary: z.object({
      agentFieldChanges: z.number().int(),
      promptsCreated: z.number().int(),
      promptsUpdated: z.number().int(),
      promptsRemoved: z.number().int(),
      skillsCreated: z.number().int(),
      skillsUpdated: z.number().int(),
      attachmentsCreated: z.number().int(),
      attachmentsUpdated: z.number().int(),
      attachmentsSkipped: z.number().int(),
      attachmentsSkippedBrandUnavailable: z.number().int(),
      settingsFieldChanges: z.number().int().optional(),
      dataSourcesCreated: z.number().int().optional(),
      dataSourcesUpdated: z.number().int().optional(),
      dataSourcesSkipped: z.number().int().optional(),
      dataQueriesCreated: z.number().int().optional(),
      dataQueriesUpdated: z.number().int().optional(),
      dataQueriesSkipped: z.number().int().optional(),
      persistedToolsCreated: z.number().int().optional(),
      persistedToolsUpdated: z.number().int().optional(),
    }),
  });

  export type Response = z.infer<typeof responseSchema>;
}
